import React, { useEffect, useMemo, useState } from 'react';
import { hikingStrings, moduleCommonStrings } from '../../content/hobbyModules';
import { getPublicErrorMessage } from '../../utils/errors';

/**
 * Renders the trail add search experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.results - results value.
 * @param {*} props.onCancel - Callback invoked when cancel occurs.
 * @param {*} props.onManualEntry - Callback invoked when manual entry occurs.
 * @param {*} props.onSearch - Callback invoked when search occurs.
 * @param {*} props.onUseTrail - Callback invoked when use trail occurs.
 * @param {*} props.isSearching - Whether is searching is enabled.
 * @param {*} props.error - Failure being normalized or reported.
 * @returns {JSX.Element} The rendered interface.
 */
export default function TrailAddSearch({ results, onCancel, onManualEntry, onSearch, onUseTrail, isSearching, error }) {
  const [query, setQuery] = useState('');
  const trimmedQuery = query.trim();
  const canSearch = trimmedQuery.length >= 2;

  useEffect(() => {
    if (!canSearch) return undefined;

    const timer = window.setTimeout(() => {
      onSearch(trimmedQuery);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [canSearch, onSearch, trimmedQuery]);

  const visibleResults = useMemo(() => (canSearch && !isSearching ? results : []), [canSearch, isSearching, results]);

  return (
    <div className="movie-add-search trail-add-search">
      <button type="button" className="recipe-back-link" onClick={onCancel}>← Back</button>

      <section className="recipe-form-section priority-section">
        <div className="form-group">
          <label>{hikingStrings.addSearch.label}</label>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={hikingStrings.addSearch.placeholder}
            autoFocus
          />
        </div>

        {isSearching && <p className="muted-copy movie-search-status">{moduleCommonStrings.actions.searching}</p>}
        {error && <p className="error-copy">Error: {getPublicErrorMessage(error)}</p>}

        <div className="movie-import-results movie-add-results trail-add-results">
          {visibleResults.map((trail) => (
            <button type="button" className="movie-import-result trail-import-result" key={trail.trailId} onClick={() => onUseTrail(trail)}>
              <div className="trail-result-marker" aria-hidden="true">+</div>
              <div>
                <h3>{trail.title}</h3>
                <p>{[trail.location, trail.difficulty, trail.distance && `${trail.distance} ${trail.distanceUnit || ''}`.trim()].filter(Boolean).join(' - ')}</p>
                <p>{trail.notes}</p>
                <div className="recipe-tags">
                  {[trail.trailType, trail.terrain, ...(trail.tags || [])].filter(Boolean).slice(0, 6).map((tag) => <span key={tag}>{tag}</span>)}
                </div>
              </div>
            </button>
          ))}
        </div>

        {canSearch && !isSearching && visibleResults.length === 0 && (
          <div className="empty-state movie-import-empty">
            <p>{hikingStrings.addSearch.empty}</p>
            <button type="button" className="btn-secondary" onClick={onManualEntry}>
              {moduleCommonStrings.actions.addManually}
            </button>
          </div>
        )}

        {!canSearch && (
          <p className="muted-copy movie-search-status">{hikingStrings.addSearch.hint}</p>
        )}
        <p className="muted-copy movie-search-status">{hikingStrings.addSearch.attribution}</p>
      </section>
    </div>
  );
}
